import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { ApiErrorMessage } from "@advosys/component-advosys-react";
import { mapPreparedInvoiceReadDtoToWriteDto } from "@advosys/component-app-server-client-js";
import { createCommand, createToolbarItemLink, createToolbarItemSection, DefaultButton, DelimitedText, DialogForm, Grow, SaveButton, StackLayout } from "@advosys/component-react";
import { createFieldState, getObjectWithValues, hasErrors, touchAndValidateAll, updateState, useValidationRules } from "@advosys/component-react-form";
import { deepEqual, hasValue } from "@advosys/component-react-utils";
import { showHelp } from "src/domains/application";
import { iconNames } from "../../dynamic-invoice-page-consts";
import { actions as dynamicInvoicePageActions } from "../../dynamic-invoice-page-store";
import { ForwardDialog } from "../forward-dialog/forward-dialog";
import { PreviewDialog } from "../preview-dialog/preview-dialog";
import { RejectDialog } from "../reject-dialog/reject-dialog";
import { k } from "./dynamic-invoice-dialog-i18n";
import { actions as dynamicInvoiceDialogActions } from "./dynamic-invoice-dialog-store";
import { InvoiceText } from "./components/invoice-text/invoice-text";
import { MainInformation } from "./components/main-information/main-information";
import { MatterLedgerEntries } from "./components/matter-ledger-entries/matter-ledger-entries";
import { TimeTrackEntries } from "./components/time-track-entries/time-track-entries";
const mapStateToProps = (state) => ({
    ...state.dynamicInvoices.dynamicInvoicesPages.dynamicInvoicesPage.dynamicInvoiceDialog,
    rejectDialogVisible: state.dynamicInvoices.dynamicInvoicesPages.dynamicInvoicesPage.page.rejectDialogVisible,
    forwardDialogVisible: state.dynamicInvoices.dynamicInvoicesPages.dynamicInvoicesPage.page.forwardDialogVisible,
    previewDialogVisible: state.dynamicInvoices.dynamicInvoicesPages.dynamicInvoicesPage.page.previewDialogVisible
});
const actions = {
    ...dynamicInvoiceDialogActions,
    hideDynamicInvoiceDialog: dynamicInvoicePageActions.hideDynamicInvoiceDialog,
    showRejectDialog: dynamicInvoicePageActions.showRejectDialog,
    showForwardDialog: dynamicInvoicePageActions.showForwardDialog,
    showPreviewDialog: dynamicInvoicePageActions.showPreviewDialog
};
const DynamicInvoiceDialogBase = (props) => {
    const { preparedInvoice, submitting } = props;
    const { t } = useTranslation();
    const { maxLength, required } = useValidationRules();
    const initialFormState = useMemo(() => {
        return {
            contactId: createFieldState(preparedInvoice.contactId),
            recipientEntityId: createFieldState(preparedInvoice.recipientEntityId),
            fee: createFieldState(preparedInvoice.fee),
            feeStatement: createFieldState(preparedInvoice.feeStatement),
            note: createFieldState(preparedInvoice.note),
            invoiceRegarding: createFieldState(preparedInvoice.invoiceRegarding?.replaceAll("\r\n", " "))
        };
    }, [preparedInvoice.contactId, preparedInvoice.recipientEntityId, preparedInvoice.fee, preparedInvoice.feeStatement, preparedInvoice.note, preparedInvoice.invoiceRegarding]);
    const [formState, setFormState] = useState(initialFormState);
    const [isCanceling, setIsCanceling] = useState(false);
    const [currency, setCurrency] = useState(preparedInvoice.currency);
    const previousInitialFormStateRef = useRef(initialFormState);
    useEffect(() => {
        const previousInitialFormState = previousInitialFormStateRef.current;
        previousInitialFormStateRef.current = initialFormState;
        setFormState(prev => {
            const next = { ...prev };
            Object.keys(initialFormState).forEach(key => {
                if (prev[key].value === previousInitialFormState[key].value) {
                    next[key] = initialFormState[key];
                }
            });
            return next;
        });
    }, [initialFormState]);
    useEffect(() => {
        return () => {
            props.clear();
        };
    }, []);
    const formController = useMemo(() => ({
        contactId: { onStateChange: state => setFormState(prev => ({ ...prev, contactId: { ...prev.contactId, ...state } })) },
        recipientEntityId: {
            onStateChange: state => setFormState(prev => ({ ...prev, recipientEntityId: { ...prev.recipientEntityId, ...state } })),
            validators: [required]
        },
        fee: { onStateChange: state => setFormState(prev => ({ ...prev, fee: { ...prev.fee, ...state } })), validators: [required] },
        feeStatement: { onStateChange: state => setFormState(prev => ({ ...prev, feeStatement: { ...prev.feeStatement, ...state } })), validators: [required] },
        note: { onStateChange: state => setFormState(prev => ({ ...prev, note: { ...prev.note, ...state } })), validators: [maxLength(2000)] },
        invoiceRegarding: { onStateChange: state => setFormState(prev => ({ ...prev, invoiceRegarding: { ...prev.invoiceRegarding, ...state } })), validators: [maxLength(2000)], }
    }), []);
    const hasUnsavedChanges = useMemo(() => !deepEqual(getObjectWithValues(formController, formState), getObjectWithValues(formController, initialFormState)), [formController, formState, initialFormState]);
    const validateChanges = () => {
        const validatedState = touchAndValidateAll(formController, formState);
        updateState(setFormState, validatedState);
        return !hasErrors(validatedState);
    };
    const handleSaveChanges = () => {
        if (validateChanges()) {
            const values = getObjectWithValues(formController, formState);
            props.savePreparedInvoiceRequest(preparedInvoice.id, { ...mapPreparedInvoiceReadDtoToWriteDto(preparedInvoice), ...values, currency });
        }
    };
    const handleApprove = () => {
        if (hasUnsavedChanges) {
            if (validateChanges()) {
                const values = getObjectWithValues(formController, formState);
                props.saveAndApprovePreparedInvoiceRequest(preparedInvoice.id, { ...mapPreparedInvoiceReadDtoToWriteDto(preparedInvoice), ...values });
            }
        }
        else {
            props.approvePreparedInvoiceRequest(preparedInvoice.id);
        }
    };
    const handleForward = () => {
        if (hasUnsavedChanges) {
            if (validateChanges()) {
                const values = getObjectWithValues(formController, formState);
                props.savePreparedInvoiceRequest(preparedInvoice.id, { ...mapPreparedInvoiceReadDtoToWriteDto(preparedInvoice), ...values, currency });
                props.showForwardDialog("dynamic-invoice-dialog");
            }
        }
        else {
            props.showForwardDialog("dynamic-invoice-dialog");
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(DialogForm, { dialogProps: {
                    size: "takeover",
                    header: _jsx(DelimitedText, { items: [t(k.dialogHeaderStart, { matterId: preparedInvoice.matterId }), preparedInvoice.clientName, t(k.dialogHeaderEnd)] }),
                    commandBarProps: { items: [createCommand("preview", t(k.previewButton), () => props.showPreviewDialog("dynamic-invoice-dialog"), iconNames.preview, submitting)] },
                    toolbarItems: [
                        {
                            icon: "Help",
                            items: [createToolbarItemSection(t(k.functionHelpText), [createToolbarItemLink(`${t(k.dynamicInvoiceDialogHelp)}`, undefined, () => showHelp("dynamicInvoiceDialog:dynamicInvoiceDialog"), false, true)])]
                        }
                    ]
                }, cancel: { onCancel: props.hideDynamicInvoiceDialog, isCanceling, setIsCanceling }, onSubmit: handleApprove, hasUnsavedChanges: hasUnsavedChanges, preventDisableSubmitButtonWhenUnsavedChanges: true, isSubmitting: submitting, submitButtonProps: { content: t(k.approveButton) }, cancelButtonProps: { content: t(k.closeButton) }, footerConent: _jsxs(StackLayout, { horizontal: true, children: [_jsx(SaveButton, { text: t(k.saveChangesButton), onClick: handleSaveChanges, disabled: !hasUnsavedChanges || submitting, renderAsDefaultButton: true }), _jsx(DefaultButton, { text: t(k.forwardButton), onClick: handleForward, disabled: submitting }), _jsx(DefaultButton, { text: t(k.rejectButton), onClick: () => props.showRejectDialog("dynamic-invoice-dialog"), disabled: !preparedInvoice.rejectPossible || submitting })] }), children: _jsxs(StackLayout, { grow: Grow.Last, childrenGap: "m", children: [hasValue(props.submitError) && _jsx(ApiErrorMessage, { message: props.submitError }), _jsxs(StackLayout, { grow: Grow.NotFirst, horizontal: true, childrenGap: "l2", children: [_jsx(MainInformation, { formController: formController, formState: formState, preparedInvoice: preparedInvoice, onCurrencyChanged: setCurrency }), _jsxs(StackLayout, { grow: Grow.Last, children: [_jsx(InvoiceText, { formController: formController, formState: formState }), _jsx(MatterLedgerEntries, { preparedInvoiceId: props.preparedInvoice.id })] }), _jsx(StackLayout, { grow: Grow.All, children: _jsx(TimeTrackEntries, { preparedInvoiceId: props.preparedInvoice.id }) })] })] }) }), props.previewDialogVisible === "dynamic-invoice-dialog" && _jsx(PreviewDialog, { preparedInvoice: preparedInvoice }), props.rejectDialogVisible === "dynamic-invoice-dialog" && _jsx(RejectDialog, { preparedInvoice: preparedInvoice }), props.forwardDialogVisible === "dynamic-invoice-dialog" && _jsx(ForwardDialog, { preparedInvoiceIds: [preparedInvoice.id] })] }));
};
export const DynamicInvoiceDialog = connect(mapStateToProps, actions)(DynamicInvoiceDialogBase);
